import type { CsvRow, OperationResult, PreviewSeries, YearlyRecordSplitArgs } from "../../types";
import { compareMonthDay, dateToMonthDay, isValidMonthDay } from "../../primitives/monthDay";
import { groupByYear } from "../../primitives/groupByYear";
import { filterByMonthDayRange } from "../../primitives/filterByMonthDayRange";
import { normalizeYear } from "../../primitives/normalizeYear";

export function applyYearlyRecordSplit(
  rows: CsvRow[],
  args: YearlyRecordSplitArgs
): OperationResult {
  if (rows.length === 0) {
    return { series: [], warnings: ["No data points in dataset"] };
  }

  const start = isValidMonthDay(args.startMonthDay) ? args.startMonthDay : null;
  const end = isValidMonthDay(args.endMonthDay) ? args.endMonthDay : null;
  const targetYear = rows.reduce(
    (max, r) => Math.max(max, r.timestamp.getUTCFullYear()),
    -Infinity
  );

  const isWrap = !!(start && end && compareMonthDay(start, end) > 0);
  const series: PreviewSeries[] = [];

  if (isWrap) {
    const grouped = groupByYear(rows);
    const startYears = Array.from(grouped.keys());
    for (const Y of startYears) {
      const tail = (grouped.get(Y) ?? []).filter(
        (r) => compareMonthDay(dateToMonthDay(r.timestamp), start!) >= 0
      );
      const head = (grouped.get(Y + 1) ?? []).filter(
        (r) => compareMonthDay(dateToMonthDay(r.timestamp), end!) <= 0
      );
      if (tail.length + head.length === 0) continue;

      const tailN = normalizeYear(tail, targetYear - 1);
      const headN = normalizeYear(head, targetYear);
      const merged = [...tailN, ...headN].sort(
        (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
      );

      series.push({
        label: `${Y}-${Y + 1}`,
        timestamps: merged.map((r) => r.timestamp),
        values: merged.map((r) => r.value),
      });
    }
  } else {
    const filtered = filterByMonthDayRange(rows, start, end);
    const grouped = groupByYear(filtered);
    for (const [year, yearRows] of Array.from(grouped.entries())) {
      const normalized = normalizeYear(yearRows, targetYear).sort(
        (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
      );
      series.push({
        label: String(year),
        timestamps: normalized.map((r) => r.timestamp),
        values: normalized.map((r) => r.value),
      });
    }
  }

  return {
    series,
    warnings: series.length === 0 ? ["No rows match the selected date window"] : undefined,
  };
}
